import { useState } from 'react'

import {
  useEventsOnLoadQuery,
  useEventsScrollCursorLazyQuery,
} from '@/graphql/generated/hooks'

import { EventFiltersI, EventsEdgeT } from '../types/types'

const EVENT_FETCH_COUNT = 25

export const useEvents = ({ incident, filters }: EventFiltersI) => {
  const [eventCursor, setEventCursor] = useState({
    scrollUp: null,
    scrollDown: null,
  })
  const [hasOlder, setHasOlder] = useState(false)
  const [events, setEvents] = useState<EventsEdgeT[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEventsOnLoadQuery({
    skip: !incident,
    errorPolicy: 'all',
    variables: {
      facilityId: filters?.facilityId,
      deviceId: filters?.deviceId,
      mainEventTimestamp: incident?.detectedAt,
      firstANDLastCount: EVENT_FETCH_COUNT,
    },
    onCompleted: (data) => {
      // On initial load set start/end cursor
      if (eventCursor.scrollUp === null) {
        setEventCursor({
          ...eventCursor,
          // The cursor to load the more recent events (on scroll up)
          scrollUp: data?.eventsAfter?.pageInfo?.startCursor,
          // The cursor to load the older events (on scroll down)
          scrollDown: data?.eventsBefore?.pageInfo?.endCursor,
        })
        setEvents([...data?.eventsAfter?.edges, ...data?.eventsBefore?.edges])
        setHasOlder(Boolean(data?.eventsBefore?.pageInfo?.endCursor))
        setIsLoading(false)
      }
    },
  })

  const [getMoreEvents] = useEventsScrollCursorLazyQuery({
    errorPolicy: 'all',
  })

  const getNewerEvents = async () => {
    setIsLoading(true)
    const { data: eventData } = await getMoreEvents({
      fetchPolicy: 'cache-and-network',
      variables: {
        last: EVENT_FETCH_COUNT,
        before: eventCursor?.scrollUp,
        facilityId: filters.facilityId,
        deviceId: filters.deviceId,
        detectedAtGte: incident.detectedAt,
      },
    })
    if (eventData?.events?.pageInfo?.startCursor) {
      setEventCursor({
        ...eventCursor,
        scrollUp: eventData?.events?.pageInfo?.startCursor,
      })
    }

    setEvents([...eventData?.events?.edges, ...events])
    setIsLoading(false)
    return Boolean(eventData?.events?.pageInfo?.startCursor)
  }

  const getOlderEvents = async () => {
    setIsLoading(true)
    const { data: eventData } = await getMoreEvents({
      variables: {
        first: EVENT_FETCH_COUNT,
        after: eventCursor?.scrollDown,
        facilityId: filters.facilityId,
        deviceId: filters.deviceId,
        detectedAtLt: incident.detectedAt,
      },
    })
    setEventCursor({
      ...eventCursor,
      scrollDown: eventData?.events?.pageInfo?.endCursor,
    })
    setHasOlder(Boolean(eventData?.events?.pageInfo?.endCursor))
    setEvents([...events, ...eventData?.events?.edges])
    setIsLoading(false)
  }

  return {
    isLoading,
    events,
    hasOlder,
    getNewerEvents,
    getOlderEvents,
  }
}
